import {
  $CombinedState,
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";

import * as api from "../../api";
import { ApiError } from "../../api/error";
import { getConfig } from "../../api/getConfig";
import { FormType } from "../../components/enums";
import { RootState } from "../../store/rootReducer";
import {
  selectACFormData,
  selectOCVehicleFormData,
} from "../../store/rootSelectors";

enum RequestStatus {
  IDLE = "idle",
  PENDING = "pending",
  SUCCESS = "success",
  ERROR = "error",
}

type SenderState = {
  caseId: string;
  msg: string;
  status: RequestStatus;
  isSent: boolean;
};

const initialState: SenderState = {
  caseId: "",
  msg: "",
  status: RequestStatus.IDLE,
  isSent: false,
};

type Endpoints = {
  apiEndpoint: string;
  apiPDFEndpoint: string;
  apiPostalCodesAndModelsEndpoint: string;
};
type PartialEndpoints = Partial<Endpoints>;

const UATHOST = "my.uat.ayvens.pl";
const DEVHOST = "portal.dev.leaseplan.pl";
const PRODHOST = "my.ayvens.pl";

const DEFAULT_PATHS = {
  api: "/Likwidacja/api/FNOL",
  pdf: "/Likwidacja/FNOL",
  postal: "/Likwidacja/Api/",
};

function hostBase(hostname: string): string | null {
  if (hostname === UATHOST) return "https://my.uat.ayvens.pl";
  if (hostname === DEVHOST) return "https://portal.dev.leaseplan.pl";
  if (hostname === PRODHOST) return "https://my.ayvens.pl";
  return null;
}

function fallbackEndpoint(kind: "api" | "pdf" | "postal"): string {
  const h = window.location.hostname;
  const base = hostBase(h) ?? window.location.origin;
  const path =
    kind === "api"
      ? DEFAULT_PATHS.api
      : kind === "pdf"
      ? DEFAULT_PATHS.pdf
      : DEFAULT_PATHS.postal;
  return `${base}${path}`;
}

function resolveEndpointFromState(
  kind: "api" | "pdf" | "postal",
  state: RootState
): string | undefined {
  const cfg = state?.appData?.config;
  if (!cfg) return undefined;
  if (kind === "api") return (cfg as any).apiEndpoint as string | undefined;
  if (kind === "pdf") return (cfg as any).apiPDFEndpoint as string | undefined;
  return (cfg as any).apiPostalCodesAndModelsEndpoint as string | undefined;
}

function resolveEndpoint(
  kind: "api" | "pdf" | "postal",
  state: RootState,
  override?: PartialEndpoints
): string {
  const fromOverride =
    kind === "api"
      ? override?.apiEndpoint
      : kind === "pdf"
      ? override?.apiPDFEndpoint
      : override?.apiPostalCodesAndModelsEndpoint;
  if (
    fromOverride &&
    typeof fromOverride === "string" &&
    fromOverride.length > 0
  )
    return fromOverride;
  const fromState = resolveEndpointFromState(kind, state);
  if (fromState && typeof fromState === "string" && fromState.length > 0)
    return fromState;
  return fallbackEndpoint(kind);
}

export const postTextData = createAsyncThunk<
  string,
  {
    data:
      | ReturnType<typeof selectACFormData>
      | ReturnType<typeof selectOCVehicleFormData>;
    formType: FormType;
    endpoints?: PartialEndpoints;
  },
  { state: RootState; rejectValue: string }
>(
  "sender/sendCase",
  async ({ data, formType, endpoints }, { getState, rejectWithValue }) => {
    try {
      const endpoint = resolveEndpoint("api", getState(), endpoints);
      const status = await api.postTextData(endpoint, data, formType);
      return status;
    } catch (error: unknown) {
      return rejectWithValue((error as ApiError).getObject().message as string);
    }
  }
);

export const postImages = createAsyncThunk<
  boolean | string,
  { type: string; images: Array<string>; endpoints?: PartialEndpoints },
  { state: RootState; rejectValue: string }
>(
  "sender/uploadImages",
  async ({ type, images, endpoints }, { getState, rejectWithValue }) => {
    try {
      const endpoint = resolveEndpoint("api", getState(), endpoints);
      const caseId = getState().sender.caseId;
      const status = await api.postImages(endpoint, caseId, type, images);
      return status;
    } catch (error: unknown) {
      return rejectWithValue((error as ApiError).getObject().message as string);
    }
  }
);

export const generatePDF = createAsyncThunk<
  boolean,
  { formType: FormType; endpoints?: PartialEndpoints },
  { state: RootState; rejectValue: string }
>(
  "sender/generatePDF",
  async ({ formType, endpoints }, { getState, rejectWithValue }) => {
    try {
      const endpoint = resolveEndpoint("pdf", getState(), endpoints);
      const caseId = getState().sender.caseId;
      await api.generatePDF(endpoint, caseId, formType);
      return true;
    } catch (error: unknown) {
      return rejectWithValue((error as ApiError).getObject().message as string);
    }
  }
);

const senderSlice = createSlice({
  name: "sender",
  initialState,
  reducers: {
    resetSender: () => initialState,
    fulfillSender: (state) => {
      state.status = RequestStatus.SUCCESS;
      state.isSent = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postTextData.pending, (state) => {
      state.status = RequestStatus.PENDING;
      state.msg = "Trwa wysyłanie formularza";
    });
    builder.addCase(postTextData.fulfilled, (state, { payload }) => {
      state.caseId = payload;
    });
    builder.addCase(postImages.pending, (state) => {
      state.msg = "Trwa wysyłanie zdjęć";
    });
    builder.addMatcher(
      isAnyOf(postTextData.rejected, postImages.rejected, generatePDF.rejected),
      (state, { payload }) => {
        state.status = RequestStatus.ERROR;
        if (payload) {
          state.msg = payload;
        }
      }
    );
  },
});

const statusSelector = (state: RootState) => state.sender.status;
const sentFlagSelector = (state: RootState) => state.sender.isSent;
export const isCaseSentSuccessfully = createSelector(
  statusSelector,
  sentFlagSelector,
  (status, sentFlag) => status === RequestStatus.SUCCESS && sentFlag
);
export const doesErrorOccured = createSelector(
  statusSelector,
  (status) => status === RequestStatus.ERROR
);

export const { fulfillSender, resetSender } = senderSlice.actions;

export const { reducer } = senderSlice;
